import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Actives, ApiService, PageData } from '@homeleite/arch';
import { BreedersApiRepository } from '../../../../../../../libs/domain/src/lib/breeders/breeders-api.repository';
import { ControllersApiRepository } from '../../../../../../../libs/domain/src/lib/controllers/controllers-api.repository';
import { ProducersApiRepository } from '../../../../../../../libs/domain/src/lib/producers/producers-api.repository';
import { UsersRepository } from '@homeleite/domain';
import { ControllerSummary } from '../../../../../../../libs/domain/src/lib/controllers/controller';
import { catchError, forkJoin, of } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { APP_VERSION } from '../../../core/app-version';
import { currentApiEnvKey, currentApiEnvironment } from '../../../core/api-environments';

type ControllersScope = 'ativos' | 'inativos' | 'todos';

const CONTROLLERS_PAGE = 10;

interface SyncCounts {
    FAILED?: number;
    PENDING?: number;
    SYNCED?: number;
    UNSENT?: number;
}

interface OutboxCounts {
    FAILED?: number;
    PENDING?: number;
    SENT?: number;
}

interface SyncItem {
    producerId?: string | null;
    producerCode?: string | null;
    producerName?: string | null;
    syncedAt?: string | null;
    lastAttemptAt?: string | null;
}

interface UsageRow {
    id: string;
    code: string;
    name: string;
    actions: number;
    lastAt?: string | null;
    route?: string[];
}

interface InactivityRow {
    id: string;
    code: string;
    name: string;
    lastLoginAt?: string | null;
    lastActionAt?: string | null;
    daysWithoutAction?: number | null;
    daysWithoutLogin?: number | null;
}

interface DashboardSummary {
    periodDays?: number;
    topProducers?: UsageRow[];
    topControllers?: UsageRow[];
    milkControlsToday?: number;
    milkControlsWeek?: number;
    milkControlsOpen?: number;
    users?: { total?: number; producer?: number; controller?: number; admin?: number };
}

@Component({
    selector: 'homeleite-admin-home',
    templateUrl: './admin-home.component.html',
    styleUrls: ['./admin-home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminHomeComponent implements OnInit {
    readonly appVersion = APP_VERSION;
    readonly envLabel = currentApiEnvironment().label;
    readonly isProductionApi = currentApiEnvKey() === 'production';

    loading = true;
    dashboardOk = false;
    integrationOk = false;
    producers = 0;
    controllersList: ControllerSummary[] = [];
    controllersScope: ControllersScope = 'ativos';
    readonly controllersScopes: ControllersScope[] = ['ativos', 'inativos', 'todos'];
    breeders = 0;
    syncFailed = 0;
    syncPending = 0;
    syncSynced = 0;
    outboxFailed = 0;
    outboxPending = 0;
    outboxSent = 0;
    divergenceCount = 0;
    divergenceOldestDays = 0;
    syncFailureCount = 0;
    successRate7d: number | null = null;
    milkToday = 0;
    milkWeek = 0;
    milkOpen = 0;
    usersTotal = 0;
    usersProducer = 0;
    usersController = 0;
    topProducers: UsageRow[] = [];
    controllersActions: Record<string, number> = {};
    controllersShown = CONTROLLERS_PAGE;
    inactivity: InactivityRow[] = [];
    inactivityOk = false;
    periodDays = 30;

    constructor(
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly api: ApiService,
        private readonly producersRepo: ProducersApiRepository,
        private readonly controllersRepo: ControllersApiRepository,
        private readonly breedersRepo: BreedersApiRepository,
        private readonly usersRepo: UsersRepository
    ) {}

    ngOnInit(): void {
        this.refresh();
    }

    get inactivityTop(): InactivityRow[] {
        return this.inactivity.slice(0, 10);
    }

    get attentionItems():{ title: string; detail: string; route: string[]; tone: 'crit' | 'warn' }[] {
        const items: { title: string; detail: string; route: string[]; tone: 'crit' | 'warn' }[] = [];
        if (!this.loading && !this.dashboardOk) {
            items.push({
                title: 'Painel completo só na Homologação',
                detail: `API ${this.envLabel} não expõe /admin-dashboard — cadastros ok; uso/controles/SRG ficam n/d`,
                route: ['/home'],
                tone: 'warn'
            });
        }
        if (!this.loading && !this.integrationOk) {
            items.push({
                title: 'SRG-SCL indisponível nesta API',
                detail: 'Troque o banner para Homologação para monitorar integração',
                route: ['/integration-settings'],
                tone: 'warn'
            });
        }
        if (this.syncFailed > 0) {
            items.push({
                title: `${this.syncFailed} animal(is) com falha na integração`,
                detail: 'Abrir monitoramento SRG-SCL',
                route: ['/integration-monitor'],
                tone: 'crit'
            });
        }
        if (this.outboxFailed > 0) {
            items.push({
                title: `${this.outboxFailed} evento(s) com erro na fila`,
                detail: 'Outbox da integração',
                route: ['/integration-monitor'],
                tone: 'crit'
            });
        }
        if (this.divergenceCount > 0) {
            items.push({
                title: `${this.divergenceCount} divergência(s) com o SRG-SCL`,
                detail: this.divergenceOldestDays > 0 ? `Mais antiga há ${this.divergenceOldestDays} dia(s)` : 'Conferir no monitoramento',
                route: ['/integration-monitor'],
                tone: 'crit'
            });
        }
        if (this.syncFailureCount > 0) {
            items.push({
                title: `${this.syncFailureCount} falha(s) de sincronização registrada(s) pelo app`,
                detail: 'Abrir falhas no monitoramento',
                route: ['/integration-monitor'],
                tone: 'crit'
            });
        }
        if (this.successRate7d !== null && this.successRate7d < 95) {
            items.push({
                title: `Taxa de sucesso do SRG-SCL em ${this.successRate7d}% (7 dias)`,
                detail: 'Ver envios por origem',
                route: ['/integration-monitor'],
                tone: 'warn'
            });
        }
        if (this.syncPending > 0) {
            items.push({
                title: `${this.syncPending} animal(is) pendente(s) de envio`,
                detail: 'Acompanhar sincronização',
                route: ['/integration-monitor'],
                tone: 'warn'
            });
        }
        if (this.outboxPending > 0) {
            items.push({
                title: `${this.outboxPending} evento(s) aguardando envio`,
                detail: 'Fila de eventos',
                route: ['/integration-monitor'],
                tone: 'warn'
            });
        }
        if (this.milkOpen > 0) {
            items.push({
                title: `${this.milkOpen} controle(s) leiteiro(s) em aberto`,
                detail: 'Acompanhar finalização no app',
                route: ['/producers'],
                tone: 'warn'
            });
        }
        if (items.length === 0 && !this.loading) {
            items.push({
                title: 'Nenhuma pendência crítica agora',
                detail: 'Integração sem falhas reportadas',
                route: ['/integration-monitor'],
                tone: 'warn'
            });
        }
        return items;
    }

    refresh(): void {
        this.loading = true;
        this.cd.markForCheck();

        forkJoin({
            producers: this.producersRepo.count().pipe(catchError(() => of(0))),
            breeders: this.breedersRepo.count().pipe(catchError(() => of(0))),
            users: this.usersRepo.count().pipe(catchError(() => of(0))),
            controllersAll: this.controllersRepo
                .page(new PageData(0, 1000), undefined, undefined, undefined, Actives.ALL)
                .pipe(catchError(() => of([] as ControllerSummary[]))),
            sync: this.api.root
                .path('integration-sync', 'items')
                .getJson<{ counts: SyncCounts; items: SyncItem[] }>({
                    params: new HttpParams().set('page', '0').set('size', '100').set('status', 'SYNCED')
                })
                .pipe(
                    timeout(12000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ counts: {} as SyncCounts, items: [] as SyncItem[], ok: false as const }))
                ),
            outbox: this.api.root
                .path('integration-sync', 'outbox')
                .getJson<{ counts: OutboxCounts }>({
                    params: new HttpParams().set('limit', '1')
                })
                .pipe(
                    timeout(12000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ counts: {} as OutboxCounts, ok: false as const }))
                ),
            divergences: this.api.root
                .path('integration-sync', 'divergences')
                .getJson<{ count: number; items: { firstDetectedAt: string | null }[] }>({
                    params: new HttpParams().set('limit', '50')
                })
                .pipe(
                    timeout(12000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ count: 0, items: [], ok: false as const }))
                ),
            syncFailures: this.api.root
                .path('integration-sync', 'sync-failures')
                .getJson<{ count: number }>({
                    params: new HttpParams().set('size', '1')
                })
                .pipe(
                    timeout(12000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ count: 0, ok: false as const }))
                ),
            metrics: this.api.root
                .path('integration-sync', 'metrics')
                .getJson<{ origins: { sent: number; failed: number }[] }>({
                    params: new HttpParams().set('days', '7')
                })
                .pipe(
                    timeout(12000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ origins: [] as { sent: number; failed: number }[], ok: false as const }))
                ),
            dashboard: this.api.root
                .path('admin-dashboard', 'summary')
                .getJson<DashboardSummary>()
                .pipe(
                    timeout(15000),
                    map((v) => ({ ...v, ok: true as const })),
                    catchError(() => of({ ok: false as const } as DashboardSummary & { ok: false }))
                ),
            inactivity: this.api.root
                .path('admin-dashboard', 'inactivity')
                .getJson<InactivityRow[]>()
                .pipe(
                    timeout(15000),
                    map((rows) => ({ rows, ok: true as const })),
                    catchError(() => of({ rows: [] as InactivityRow[], ok: false as const }))
                )
        })
            .pipe(
                map((r) => {
                    const dash = r.dashboard;
                    const dashboardOk = dash.ok === true;
                    const integrationOk = r.sync.ok && r.outbox.ok;

                    const topProducers =
                        dashboardOk && dash.topProducers?.length
                            ? dash.topProducers.map((p) => ({
                                  ...p,
                                  route: p.id ? ['/producers', p.id] : ['/producers']
                              }))
                            : integrationOk
                              ? this.aggregateProducersFromSync(r.sync.items || [])
                              : [];

                    return {
                        dashboardOk,
                        integrationOk,
                        producers: r.producers || 0,
                        controllersList: r.controllersAll || [],
                        breeders: r.breeders || 0,
                        syncFailed: integrationOk ? r.sync.counts?.FAILED || 0 : 0,
                        syncPending: integrationOk ? r.sync.counts?.PENDING || 0 : 0,
                        syncSynced: integrationOk ? r.sync.counts?.SYNCED || 0 : 0,
                        outboxFailed: integrationOk ? r.outbox.counts?.FAILED || 0 : 0,
                        outboxPending: integrationOk ? r.outbox.counts?.PENDING || 0 : 0,
                        outboxSent: integrationOk ? r.outbox.counts?.SENT || 0 : 0,
                        divergenceCount: integrationOk && r.divergences.ok ? r.divergences.count || 0 : 0,
                        divergenceOldestDays:
                            integrationOk && r.divergences.ok && r.divergences.items.length
                                ? Math.floor(
                                      (Date.now() - Math.min(...r.divergences.items.map((d) => new Date(d.firstDetectedAt ?? 0).getTime()))) /
                                          86400000
                                  )
                                : 0,
                        syncFailureCount: integrationOk && r.syncFailures.ok ? r.syncFailures.count || 0 : 0,
                        successRate7d: this.successRateOf(integrationOk && r.metrics.ok ? r.metrics.origins : []),
                        milkToday: dashboardOk ? dash.milkControlsToday || 0 : 0,
                        milkWeek: dashboardOk ? dash.milkControlsWeek || 0 : 0,
                        milkOpen: dashboardOk ? dash.milkControlsOpen || 0 : 0,
                        usersTotal: dashboardOk ? dash.users?.total || 0 : r.users || 0,
                        usersProducer: dashboardOk ? dash.users?.producer || 0 : 0,
                        usersController: dashboardOk ? dash.users?.controller || 0 : 0,
                        topProducers,
                        controllersActions: Object.fromEntries(
                            ((dashboardOk && dash.topControllers) || []).map((c) => [c.id, c.actions])
                        ),
                        inactivity: r.inactivity.ok ? r.inactivity.rows : [],
                        inactivityOk: r.inactivity.ok,
                        periodDays: (dashboardOk && dash.periodDays) || 30
                    };
                })
            )
            .subscribe({
                next: (r) => {
                    Object.assign(this, r);
                    this.loading = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.loading = false;
                    this.cd.markForCheck();
                }
            });
    }

    /** Enviados ÷ (enviados + com erro) somando todas as origens. Null sem conclusões no período. */
    private successRateOf(origins: { sent: number; failed: number }[]): number | null {
        const sent = origins.reduce((n, o) => n + o.sent, 0);
        const done = sent + origins.reduce((n, o) => n + o.failed, 0);
        return done === 0 ? null : Math.round((sent / done) * 100);
    }

    private aggregateProducersFromSync(items: SyncItem[]): UsageRow[] {
        const map = new Map<string, UsageRow>();
        for (const it of items) {
            const key = it.producerId || it.producerCode || it.producerName;
            if (!key) continue;
            const cur = map.get(key) || {
                id: it.producerId || '',
                code: it.producerCode || '—',
                name: it.producerName || 'Produtor',
                actions: 0,
                lastAt: null as string | null,
                route: it.producerId ? ['/producers', it.producerId] : ['/producers']
            };
            cur.actions += 1;
            const ts = it.syncedAt || it.lastAttemptAt;
            if (ts && (!cur.lastAt || ts > cur.lastAt)) cur.lastAt = ts;
            map.set(key, cur);
        }
        return [...map.values()].sort((a, b) => b.actions - a.actions).slice(0, 8);
    }

    go(path: string | string[]): void {
        this.router.navigate(Array.isArray(path) ? path : [path]);
    }

    navigateToProducers(): void {
        this.go('/producers');
    }

    get controllersInScope(): ControllerSummary[] {
        return this.controllersList.filter(
            (c) => this.controllersScope === 'todos' || c.active === (this.controllersScope === 'ativos')
        );
    }

    get controllersKpi(): { linked: number; total: number } {
        const list = this.controllersInScope;
        return { linked: list.filter((c) => c.producersCount > 0).length, total: list.length };
    }

    get controllersRanking(): {
        id: string;
        name: string;
        code: string;
        active: boolean;
        producers: number;
        actions: number;
    }[] {
        return this.controllersInScope
            .map((c) => ({
                id: c.id,
                name: c.name || 'Controlador',
                code: c.code || '—',
                active: c.active,
                producers: c.producersCount || 0,
                actions: this.controllersActions[c.id] || 0
            }))
            .sort((a, b) => b.actions - a.actions || b.producers - a.producers || a.name.localeCompare(b.name));
    }

    get controllersScopeHint(): string {
        return { ativos: 'ativos', inativos: 'inativos', todos: 'cadastrados' }[this.controllersScope];
    }

    setControllersScope(scope: ControllersScope): void {
        this.controllersScope = scope;
        this.controllersShown = CONTROLLERS_PAGE;
    }

    showMoreControllers(): void {
        this.controllersShown += CONTROLLERS_PAGE;
    }

    navigateToControllers(): void {
        this.go('/controller');
    }

    navigateToBreeders(): void {
        this.go('/breeders');
    }

    navigateToMonitor(): void {
        this.go('/integration-monitor');
    }

    navigateToSettings(): void {
        this.go('/integration-settings');
    }
}
